import React, { useState } from 'react';
import { Head, Link, router } from '@inertiajs/react';
import Layout from '../../components/Layout';
import { 
  Activity, 
  User, 
  Calendar, 
  Filter, 
  Search,
  Clock,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  FileText,
  UserPlus,
  UserCheck,
  Trash2,
  LogIn,
  LogOut,
  FolderPlus,
  Upload,
  Shield,
  RotateCcw,
  Edit3,
  Move,
  Folder,
  Settings,
  X,
  FolderX,
  FileX,
  Copy,
  PenTool,
  MessageCircle,
  MessageSquare,
  MessageSquarePlus,
  MessageSquareX,
  MessageCircleMore,
  Edit2,
  Package,
  Plus,
  Type,
  Mail,
  Link2,
  Globe,
  MapPin,
  Building2,
  Users,
  Layers,
  RefreshCw,
  Info,
  Check,
  ChevronsUpDown,
  Map,
  Building,
  UserCircle,
  TrendingUp,
  Tag
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { DatePicker } from '../../components/ui/date-picker';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Button } from '../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import { cn } from '../../lib/utils';

interface LogActivityUser {
  id: number;
  email: string;
  name: string;
}

interface LogActivity {
  id: number;
  user: LogActivityUser;
  action: string;
  description: string;
  performed_at: string;
  time_ago: string;
}

interface FilterOptions {
  users: { id: number; email: string }[];
  action_types: { value: string; label: string }[];
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

interface IndexProps {
  log_activities: LogActivity[];
  filters: {
    search?: string;
    user_id?: string;
    action_type?: string;
    date_from?: string;
    date_to?: string;
  };
  filter_options: FilterOptions;
  pagination: Pagination;
}

function Index({ log_activities, filters, filter_options, pagination }: IndexProps) {
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const [actionTypeOpen, setActionTypeOpen] = useState(false);
  const [actionTypeSearch, setActionTypeSearch] = useState('');

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'user_login':
        return <LogIn className="w-4 h-4" />;
      case 'user_logout':
        return <LogOut className="w-4 h-4" />;
      case 'user_invited':
        return <UserPlus className="w-4 h-4" />;
      case 'user_updated':
        return <Edit3 className="w-4 h-4" />;
      case 'role_changed':
        return <Shield className="w-4 h-4" />;
      case 'user_deleted':
        return <Trash2 className="w-4 h-4" />;
      case 'template_uploaded':
        return <Upload className="w-4 h-4" />;
      case 'template_deleted':
        return <FileX className="w-4 h-4" />;
      case 'template_moved':
        return <Move className="w-4 h-4" />;
      case 'template_edited':
        return <Edit3 className="w-4 h-4" />;
      case 'folder_created':
        return <FolderPlus className="w-4 h-4" />;
      case 'folder_updated':
        return <Folder className="w-4 h-4" />;
      case 'folder_deleted':
        return <FolderX className="w-4 h-4" />;
      case 'template_copied':
        return <Copy className="w-4 h-4" />;
      case 'template_renamed':
        return <PenTool className="w-4 h-4" />;
      case 'discussion_created':
        return <MessageSquarePlus className="w-4 h-4" />;
      case 'discussion_updated':
        return <Edit2 className="w-4 h-4" />;
      case 'discussion_deleted':
        return <MessageSquareX className="w-4 h-4" />;
      case 'discussion_comment_created':
        return <MessageCircle className="w-4 h-4" />;
      case 'discussion_comment_updated':
        return <MessageCircleMore className="w-4 h-4" />;
      case 'discussion_comment_deleted':
        return <Trash2 className="w-4 h-4" />;
      // Offer activities
      case 'offer_created':
        return <Plus className="w-4 h-4" />;
      case 'offer_updated':
        return <Edit3 className="w-4 h-4" />;
      case 'offer_deleted':
        return <Trash2 className="w-4 h-4" />;
      case 'offer_renamed':
        return <PenTool className="w-4 h-4" />;
      case 'offer_status_changed':
        return <RefreshCw className="w-4 h-4" />;
      case 'offer_vertical_changed':
        return <Layers className="w-4 h-4" />;
      case 'offer_country_changed':
        return <MapPin className="w-4 h-4" />;
      case 'offer_description_updated':
        return <FileText className="w-4 h-4" />;
      case 'offer_provider_changed':
        return <Building2 className="w-4 h-4" />;
      case 'offer_client_changed':
        return <Users className="w-4 h-4" />;
      // Subject line activities
      case 'subject_line_created':
        return <Type className="w-4 h-4" />;
      case 'subject_line_updated':
        return <Edit3 className="w-4 h-4" />;
      case 'subject_line_deleted':
        return <Trash2 className="w-4 h-4" />;
      case 'subject_line_tags_updated':
        return <Edit2 className="w-4 h-4" />;
      // From line activities
      case 'from_line_created':
        return <Mail className="w-4 h-4" />;
      case 'from_line_updated':
        return <Edit3 className="w-4 h-4" />;
      case 'from_line_deleted':
        return <Trash2 className="w-4 h-4" />;
      // Footer activities
      case 'footer_created':
        return <FileText className="w-4 h-4" />;
      case 'footer_updated':
        return <Edit3 className="w-4 h-4" />;
      case 'footer_renamed':
        return <PenTool className="w-4 h-4" />;
      case 'footer_description_updated':
        return <Info className="w-4 h-4" />;
      case 'footer_deleted':
        return <Trash2 className="w-4 h-4" />;
      // Tracking link activities
      case 'tracking_link_created':
        return <Link2 className="w-4 h-4" />;
      case 'tracking_link_updated':
        return <Edit3 className="w-4 h-4" />;
      case 'tracking_link_renamed':
        return <PenTool className="w-4 h-4" />;
      case 'tracking_link_url_updated':
        return <Globe className="w-4 h-4" />;
      case 'tracking_link_deleted':
        return <Trash2 className="w-4 h-4" />;
      // Country activities
      case 'country_created':
        return <Plus className="w-4 h-4" />;
      case 'country_updated':
        return <Edit3 className="w-4 h-4" />;
      case 'country_deleted':
        return <Trash2 className="w-4 h-4" />;
      // Provider activities
      case 'provider_created':
        return <Plus className="w-4 h-4" />;
      case 'provider_updated':
        return <Edit3 className="w-4 h-4" />;
      case 'provider_deleted':
        return <Trash2 className="w-4 h-4" />;
      // Client activities
      case 'client_created':
        return <Plus className="w-4 h-4" />;
      case 'client_updated':
        return <Edit3 className="w-4 h-4" />;
      case 'client_deleted':
        return <Trash2 className="w-4 h-4" />;
      // Vertical activities
      case 'vertical_created':
        return <Plus className="w-4 h-4" />;
      case 'vertical_updated':
        return <Edit3 className="w-4 h-4" />;
      case 'vertical_deleted':
        return <Trash2 className="w-4 h-4" />;
      // Tag activities
      case 'tag_created':
        return <Plus className="w-4 h-4" />;
      case 'tag_updated':
        return <Edit3 className="w-4 h-4" />;
      case 'tag_deleted':
        return <Trash2 className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'user_login':
        return 'bg-green-100 text-green-800';
      case 'user_logout':
        return 'bg-gray-100 text-gray-800';
      case 'user_invited':
        return 'bg-blue-100 text-blue-800';
      case 'user_updated':
        return 'bg-amber-100 text-amber-800';
      case 'role_changed':
        return 'bg-purple-100 text-purple-800';
      case 'user_deleted':
        return 'bg-red-100 text-red-800';
      case 'template_uploaded':
        return 'bg-indigo-100 text-indigo-800';
      case 'template_deleted':
        return 'bg-red-100 text-red-800';
      case 'template_moved':
        return 'bg-blue-100 text-blue-800';
      case 'template_edited':
        return 'bg-yellow-100 text-yellow-800';
      case 'folder_created':
        return 'bg-green-100 text-green-800';
      case 'folder_updated':
        return 'bg-yellow-100 text-yellow-800';
      case 'folder_deleted':
        return 'bg-red-100 text-red-800';
      case 'template_copied':
        return 'bg-blue-100 text-blue-800';
      case 'template_renamed':
        return 'bg-purple-100 text-purple-800';
      case 'discussion_created':
        return 'bg-emerald-100 text-emerald-800';
      case 'discussion_updated':
        return 'bg-amber-100 text-amber-800';
      case 'discussion_deleted':
        return 'bg-red-100 text-red-800';
      case 'discussion_comment_created':
        return 'bg-sky-100 text-sky-800';
      case 'discussion_comment_updated':
        return 'bg-orange-100 text-orange-800';
      case 'discussion_comment_deleted':
        return 'bg-red-100 text-red-800';
      // Offer activities
      case 'offer_created':
        return 'bg-green-100 text-green-800';
      case 'offer_updated':
        return 'bg-blue-100 text-blue-800';
      case 'offer_deleted':
        return 'bg-red-100 text-red-800';
      case 'offer_renamed':
        return 'bg-purple-100 text-purple-800';
      case 'offer_status_changed':
        return 'bg-yellow-100 text-yellow-800';
      case 'offer_vertical_changed':
        return 'bg-indigo-100 text-indigo-800';
      case 'offer_country_changed':
        return 'bg-teal-100 text-teal-800';
      case 'offer_description_updated':
        return 'bg-blue-100 text-blue-800';
      case 'offer_provider_changed':
        return 'bg-violet-100 text-violet-800';
      case 'offer_client_changed':
        return 'bg-cyan-100 text-cyan-800';
      // Subject line activities
      case 'subject_line_created':
        return 'bg-emerald-100 text-emerald-800';
      case 'subject_line_updated':
        return 'bg-amber-100 text-amber-800';
      case 'subject_line_deleted':
        return 'bg-red-100 text-red-800';
      case 'subject_line_tags_updated':
        return 'bg-blue-100 text-blue-800';
      // From line activities
      case 'from_line_created':
        return 'bg-sky-100 text-sky-800';
      case 'from_line_updated':
        return 'bg-orange-100 text-orange-800';
      case 'from_line_deleted':
        return 'bg-red-100 text-red-800';
      // Footer activities
      case 'footer_created':
        return 'bg-lime-100 text-lime-800';
      case 'footer_updated':
        return 'bg-yellow-100 text-yellow-800';
      case 'footer_renamed':
        return 'bg-purple-100 text-purple-800';
      case 'footer_description_updated':
        return 'bg-blue-100 text-blue-800';
      case 'footer_deleted':
        return 'bg-red-100 text-red-800';
      // Tracking link activities
      case 'tracking_link_created':
        return 'bg-indigo-100 text-indigo-800';
      case 'tracking_link_updated':
        return 'bg-amber-100 text-amber-800';
      case 'tracking_link_renamed':
        return 'bg-purple-100 text-purple-800';
      case 'tracking_link_url_updated':
        return 'bg-teal-100 text-teal-800';
      case 'tracking_link_deleted':
        return 'bg-red-100 text-red-800';
      // Country activities
      case 'country_created':
        return 'bg-emerald-100 text-emerald-800';
      case 'country_updated':
        return 'bg-amber-100 text-amber-800';
      case 'country_deleted':
        return 'bg-red-100 text-red-800';
      // Provider activities
      case 'provider_created':
        return 'bg-violet-100 text-violet-800';
      case 'provider_updated':
        return 'bg-purple-100 text-purple-800';
      case 'provider_deleted':
        return 'bg-red-100 text-red-800';
      // Client activities
      case 'client_created':
        return 'bg-cyan-100 text-cyan-800';
      case 'client_updated':
        return 'bg-sky-100 text-sky-800';
      case 'client_deleted':
        return 'bg-red-100 text-red-800';
      // Vertical activities
      case 'vertical_created':
        return 'bg-indigo-100 text-indigo-800';
      case 'vertical_updated':
        return 'bg-blue-100 text-blue-800';
      case 'vertical_deleted':
        return 'bg-red-100 text-red-800';
      // Tag activities
      case 'tag_created':
        return 'bg-lime-100 text-lime-800';
      case 'tag_updated':
        return 'bg-green-100 text-green-800';
      case 'tag_deleted':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getBadgeDotColor = (action: string) => {
    switch (action) {
      case 'user_login':
        return 'bg-green-600';
      case 'user_logout':
        return 'bg-gray-600';
      case 'user_invited':
        return 'bg-blue-600';
      case 'user_updated':
        return 'bg-amber-600';
      case 'role_changed':
        return 'bg-purple-600';
      case 'user_deleted':
        return 'bg-red-600';
      case 'template_uploaded':
        return 'bg-indigo-600';
      case 'template_deleted':
        return 'bg-red-600';
      case 'template_moved':
        return 'bg-blue-600';
      case 'template_edited':
        return 'bg-yellow-600';
      case 'folder_created':
        return 'bg-green-600';
      case 'folder_updated':
        return 'bg-yellow-600';
      case 'folder_deleted':
        return 'bg-red-600';
      case 'template_copied':
        return 'bg-blue-600';
      case 'template_renamed':
        return 'bg-purple-600';
      case 'discussion_created':
        return 'bg-emerald-600';
      case 'discussion_updated':
        return 'bg-amber-600';
      case 'discussion_deleted':
        return 'bg-red-600';
      case 'discussion_comment_created':
        return 'bg-sky-600';
      case 'discussion_comment_updated':
        return 'bg-orange-600';
      case 'discussion_comment_deleted':
        return 'bg-red-600';
      // Offer activities
      case 'offer_created':
        return 'bg-green-600';
      case 'offer_updated':
        return 'bg-blue-600';
      case 'offer_deleted':
        return 'bg-red-600';
      case 'offer_renamed':
        return 'bg-purple-600';
      case 'offer_status_changed':
        return 'bg-yellow-600';
      case 'offer_vertical_changed':
        return 'bg-indigo-600';
      case 'offer_country_changed':
        return 'bg-teal-600';
      case 'offer_description_updated':
        return 'bg-blue-600';
      case 'offer_provider_changed':
        return 'bg-violet-600';
      case 'offer_client_changed':
        return 'bg-cyan-600';
      // Subject line activities
      case 'subject_line_created':
        return 'bg-emerald-600';
      case 'subject_line_updated':
        return 'bg-amber-600';
      case 'subject_line_deleted':
        return 'bg-red-600';
      case 'subject_line_tags_updated':
        return 'bg-blue-600';
      // From line activities
      case 'from_line_created':
        return 'bg-sky-600';
      case 'from_line_updated':
        return 'bg-orange-600';
      case 'from_line_deleted':
        return 'bg-red-600';
      // Footer activities
      case 'footer_created':
        return 'bg-lime-600';
      case 'footer_updated':
        return 'bg-yellow-600';
      case 'footer_renamed':
        return 'bg-purple-600';
      case 'footer_description_updated':
        return 'bg-blue-600';
      case 'footer_deleted':
        return 'bg-red-600';
      // Tracking link activities
      case 'tracking_link_created':
        return 'bg-indigo-600';
      case 'tracking_link_updated':
        return 'bg-amber-600';
      case 'tracking_link_renamed':
        return 'bg-purple-600';
      case 'tracking_link_url_updated':
        return 'bg-teal-600';
      case 'tracking_link_deleted':
        return 'bg-red-600';
      // Country activities
      case 'country_created':
        return 'bg-emerald-600';
      case 'country_updated':
        return 'bg-amber-600';
      case 'country_deleted':
        return 'bg-red-600';
      // Provider activities
      case 'provider_created':
        return 'bg-violet-600';
      case 'provider_updated':
        return 'bg-purple-600';
      case 'provider_deleted':
        return 'bg-red-600';
      // Client activities
      case 'client_created':
        return 'bg-cyan-600';
      case 'client_updated':
        return 'bg-sky-600';
      case 'client_deleted':
        return 'bg-red-600';
      // Vertical activities
      case 'vertical_created':
        return 'bg-indigo-600';
      case 'vertical_updated':
        return 'bg-blue-600';
      case 'vertical_deleted':
        return 'bg-red-600';
      // Tag activities
      case 'tag_created':
        return 'bg-lime-600';
      case 'tag_updated':
        return 'bg-green-600';
      case 'tag_deleted':
        return 'bg-red-600';
      default:
        return 'bg-gray-600';
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const applyFilters = () => {
    router.get('/log_activities', localFilters, { preserveState: true });
  };

  const clearFilters = () => {
    setLocalFilters({});
    router.get('/log_activities', {}, { preserveState: true });
  };

  const handlePageChange = (page: number) => {
    router.get('/log_activities', { ...filters, page }, { preserveState: true });
  };

  return (
    <>
      <Head title="Activity Log | Mailkit - Resolute" />
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Activity Log
            </h1>
            <p className="mt-2 text-slate-600">Track all system activities and user actions</p>
          </div>
          <div className="flex items-center gap-4">
          </div>
        </div>
      </div>

        {/* Filters Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-white/50 mb-6">
        <div className="px-4 py-3">
          <button 
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="flex items-center justify-between w-full text-left"
          >
            <div className="flex items-center">
              <Filter className="w-4 h-4 mr-2 text-gray-500" />
              <h3 className="text-base font-semibold text-gray-900">Filters</h3>
            </div>
            {filtersExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-500" />
            )}
          </button>
        </div>
        
        {filtersExpanded && (
          <div className="px-4 pb-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    className="pl-10 w-full rounded-lg border border-gray-300 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Search activities..."
                    value={localFilters.search || ''}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        applyFilters();
                      }
                    }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                <Select value={localFilters.user_id || ''} onValueChange={(value) => handleFilterChange('user_id', value)}>
                  <SelectTrigger className="w-full" size="sm">
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    {filter_options.users.map(user => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Action Type</label>
                <Popover open={actionTypeOpen} onOpenChange={setActionTypeOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={actionTypeOpen}
                      className="w-full justify-between text-sm h-9 font-normal overflow-hidden"
                    >
                      <span className={`truncate text-left ${!localFilters.action_type ? 'text-gray-500' : ''}`}>
                        {localFilters.action_type
                          ? filter_options.action_types.find(type => type.value === localFilters.action_type)?.label
                          : "All Actions"}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[350px] p-0" align="start">
                    <Command>
                      <div className="overflow-visible">
                        <CommandInput 
                          placeholder="Search actions..." 
                          value={actionTypeSearch}
                          onValueChange={setActionTypeSearch}
                          className="overflow-visible"
                        />
                      </div>
                      <CommandList>
                        <CommandEmpty>No action found.</CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value=""
                            onSelect={() => {
                              handleFilterChange('action_type', '');
                              setActionTypeOpen(false);
                              setActionTypeSearch('');
                            }}
                            className="cursor-pointer"
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4 shrink-0",
                                !localFilters.action_type ? "opacity-100" : "opacity-0"
                              )}
                            />
                            All Actions
                          </CommandItem>
                          {filter_options.action_types
                            .filter(type => 
                              type.label.toLowerCase().includes(actionTypeSearch.toLowerCase()) ||
                              type.value.toLowerCase().includes(actionTypeSearch.toLowerCase())
                            )
                            .map(type => (
                              <CommandItem
                                key={type.value}
                                value={type.value}
                                onSelect={() => {
                                  handleFilterChange('action_type', type.value);
                                  setActionTypeOpen(false);
                                  setActionTypeSearch('');
                                }}
                                className="cursor-pointer"
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4 shrink-0",
                                    localFilters.action_type === type.value ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex items-center gap-2 flex-1">
                                  <div className={`p-1.5 rounded-lg [&>svg]:!text-current ${getActionColor(type.value)}`}>
                                    {getActionIcon(type.value)}
                                  </div>
                                  <span>{type.label}</span>
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <DatePicker
                  value={localFilters.date_from}
                  onChange={(value) => handleFilterChange('date_from', value || '')}
                  placeholder="Select from date"
                  className="w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <DatePicker
                  value={localFilters.date_to}
                  onChange={(value) => handleFilterChange('date_to', value || '')}
                  placeholder="Select to date"
                  className="w-full text-sm"
                />
              </div>
            </div>
            
              <div className="mt-4 flex gap-2">
                <button
                  onClick={applyFilters}
                  className="bg-gradient-to-b from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm px-3 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-1 cursor-pointer"
                >
                  <Filter className="w-3 h-3" />
                  Apply Filters
                </button>
                <button
                  onClick={clearFilters}
                  className="border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-1 cursor-pointer"
                >
                  <RotateCcw className="w-3 h-3" />
                  Clear All
                </button>
              </div>
          </div>
        )}
      </div>

        {/* Activities List */}
        <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-white/50 overflow-hidden">
          <div className="divide-y divide-slate-100">
            {log_activities.length === 0 ? (
              <div className="py-12 px-6 text-center">
                <div className="p-3 rounded-full bg-slate-100 w-fit mx-auto mb-4">
                  <Activity className="w-6 h-6 text-slate-400" />
                </div>
                <h3 className="text-base font-medium text-slate-900 mb-2">No activities found</h3>
                <p className="text-sm text-slate-500">Try adjusting your filters or search terms</p>
              </div>
          ) : (
              log_activities.map((activity) => (
                <div key={activity.id} className="px-6 py-3 hover:bg-slate-50/50 transition-all duration-200 group">
                  <div className="flex items-start gap-3">
                    {/* Icon */}
                    <div className={`p-1.5 rounded-lg ${getActionColor(activity.action)}`}>
                      {getActionIcon(activity.action)}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-900">{activity.user.name}</span>
                        <span className="text-slate-500 text-sm">({activity.user.email})</span>
                      </div>
                      <p className="text-slate-700 text-sm">{activity.description}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {activity.performed_at}
                        </span>
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {activity.time_ago}
                        </span>
                      </div>
                    </div>

                    {/* Action Badge */}
                    <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getActionColor(activity.action)}`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${getBadgeDotColor(activity.action)}`} />
                      {activity.action.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </div>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="bg-white border border-gray-200 rounded-lg mt-4 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing <span className="font-medium">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)}
                </span> of{' '}
                <span className="font-medium">{pagination.total_count}</span> activities
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.total_pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.current_page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.current_page >= pagination.total_pages - 2) {
                      pageNum = pagination.total_pages - 4 + i;
                    } else {
                      pageNum = pagination.current_page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium border rounded-md ${
                          pageNum === pagination.current_page
                            ? 'bg-gradient-to-b from-blue-500 to-blue-600 text-white border-blue-600'
                            : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.total_pages}
                  className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

Index.layout = page => <Layout>{page}</Layout>;

export default Index;